import { defineStore } from "pinia";
import ApiManager from "~/code/api-manager";
import { EventBus } from "~/code/event-bus";
import {
  SidebarItemDto,
  CreateEditSidebarItemDto,
  SidebarItemTreeDto,
} from "@/generated/models";

type StoreApiError = {
  code?: string;
  description?: string;
};

export const useSidebarItemStore = defineStore("SidebarItem", {
  state: () => ({
    sidebarItems: [] as SidebarItemDto[],
    sidebarTree: [] as SidebarItemTreeDto[],
    visibleSidebarTree: [] as SidebarItemTreeDto[],
    selectedSidebarItem: null as SidebarItemDto | null,
    loading: false,
    errors: [] as any[],
    totalItems: 0,
    apiManager:
      ApiManager.getInstance().apis["SidebarItemApi"] ||
      ApiManager.getInstance().apis["SidebarItemsApi"] ||
      null,
  }),
  actions: {
    resolveSidebarApi() {
      if (this.apiManager) {
        return this.apiManager;
      }

      const resolvedApi =
        ApiManager.getInstance().apis["SidebarItemApi"] ||
        ApiManager.getInstance().apis["SidebarItemsApi"] ||
        null;

      this.apiManager = resolvedApi;
      return resolvedApi;
    },

    async addSidebarItem(sidebarItem: CreateEditSidebarItemDto) {
      this.loading = true;
      try {
        const sidebarApi = this.resolveSidebarApi();
        if (!sidebarApi) {
          throw new Error("SidebarItem API client is unavailable");
        }

        if (sidebarItem.title) {
          sidebarItem.title = sidebarItem.title.trim();
        }

        if (sidebarItem.icon) {
          sidebarItem.icon = sidebarItem.icon.trim();
        }

        if (sidebarItem.link) {
          sidebarItem.link = sidebarItem.link.trim();
        }

        const response = await sidebarApi.apiSidebarItemCreatePut(sidebarItem);

        if (response.status === 200) {
          this.sidebarItems.push(response.data);
          this.totalItems = this.totalItems + 1;

          EventBus.emit("toast", {
            type: "success",
            message: "Sidebar item added.",
          });

          return response.data;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error adding Sidebar Item: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught adding Sidebar Item: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteSidebarItem(sidebarItem: SidebarItemDto) {
      this.loading = true;
      try {
        if (!sidebarItem.id) {
          EventBus.emit("toast", {
            type: "error",
            message: "Error deleting Sidebar Item: missing id.",
          });
          return;
        }

        const response = await this.apiManager.apiSidebarItemDeleteDelete(
          sidebarItem.id,
        );

        if (response.status === 200) {
          this.sidebarItems = this.sidebarItems.filter(
            (r) => r.id !== sidebarItem.id,
          );

          this.sidebarTree = this.removeFromTree(
            this.sidebarTree,
            sidebarItem.id,
          );

          this.visibleSidebarTree = this.removeFromTree(
            this.visibleSidebarTree,
            sidebarItem.id,
          );

          this.totalItems = this.totalItems - 1;

          EventBus.emit("toast", {
            type: "success",
            message: "Sidebar item deleted.",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error deleting Sidebar Item: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught deleting Sidebar Item: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteSidebarItemWithChildren(sidebarItem: SidebarItemDto) {
      this.loading = true;
      try {
        if (!sidebarItem.id) {
          EventBus.emit("toast", {
            type: "error",
            message: "Error deleting Sidebar Item tree: missing id.",
          });
          return;
        }

        const response =
          await this.apiManager.apiSidebarItemDeleteWithChildrenDelete(
            sidebarItem.id,
          );

        if (response.status === 200) {
          await this.fetchSidebarItems();
          await this.fetchSidebarTree();
          await this.fetchVisibleSidebarTree();

          EventBus.emit("toast", {
            type: "success",
            message: "Sidebar item and children deleted.",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error deleting Sidebar Item tree: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught deleting Sidebar Item tree: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async editSidebarItem(sidebarItem: CreateEditSidebarItemDto) {
      this.loading = true;
      try {
        if (!sidebarItem.id) {
          EventBus.emit("toast", {
            type: "error",
            message: "Error editing Sidebar Item: missing id.",
          });
          return;
        }

        if (sidebarItem.title) {
          sidebarItem.title = sidebarItem.title.trim();
        }

        if (sidebarItem.icon) {
          sidebarItem.icon = sidebarItem.icon.trim();
        }

        if (sidebarItem.link) {
          sidebarItem.link = sidebarItem.link.trim();
        }

        const response =
          await this.apiManager.apiSidebarItemEditPost(sidebarItem);

        if (response.status === 200) {
          const index = this.sidebarItems.findIndex(
            (r) => r.id === sidebarItem.id,
          );

          if (index >= 0) {
            this.sidebarItems.splice(index, 1, response.data);
          }

          this.selectedSidebarItem = response.data;

          EventBus.emit("toast", {
            type: "success",
            message: "Sidebar item edited.",
          });

          return response.data;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error editing Sidebar Item: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught editing Sidebar Item: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchSidebarItem(id: string) {
      this.loading = true;
      try {
        const response = await this.apiManager.apiSidebarItemGetGet(id);

        if (response.status === 200 && response.data) {
          this.selectedSidebarItem = response.data;
          return this.selectedSidebarItem;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching Sidebar Item: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught fetching Sidebar Item: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchSidebarItems() {
      this.loading = true;
      try {
        const response = await this.apiManager.apiSidebarItemGetAllGet();

        if (response.status === 200 && response.data) {
          this.sidebarItems = response.data || [];
          this.totalItems = this.sidebarItems.length;

          return this.sidebarItems;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching Sidebar Items: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
            this.totalItems = 0;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught fetching Sidebar Items: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchSidebarTree() {
      this.loading = true;
      try {
        const sidebarApi = this.resolveSidebarApi();
        if (!sidebarApi) {
          this.sidebarTree = [];
          return this.sidebarTree;
        }

        const response = await sidebarApi.apiSidebarItemGetTreeGet();

        if (response.status === 200 && response.data) {
          this.sidebarTree = response.data || [];
          return this.sidebarTree;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching Sidebar Tree: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught fetching Sidebar Tree: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchVisibleSidebarTree() {
      this.loading = true;
      try {
        const sidebarApi = this.resolveSidebarApi();
        if (!sidebarApi) {
          this.visibleSidebarTree = [];
          return this.visibleSidebarTree;
        }

        const response = await sidebarApi.apiSidebarItemGetVisibleTreeGet();

        if (response.status === 200 && response.data) {
          this.visibleSidebarTree = response.data || [];
          return this.visibleSidebarTree;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching visible Sidebar Tree: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught fetching visible Sidebar Tree: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    async reorderSidebarItems(sidebarItems: any[]) {
      try {
        const response =
          await this.apiManager.apiSidebarItemReorderPost(sidebarItems);

        if (response.status === 200) {
          this.applySidebarReorderLocally(sidebarItems);

          return response.data;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error: StoreApiError) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error saving Sidebar order: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error: any) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error caught saving Sidebar order: ${error.message}`,
        });
      }
    },
    setSelectedSidebarItem(sidebarItem: SidebarItemDto | null) {
      this.selectedSidebarItem = sidebarItem;
    },

    clearSelectedSidebarItem() {
      this.selectedSidebarItem = null;
    },

    removeFromTree(
      sidebarTree: SidebarItemTreeDto[],
      sidebarItemId: string,
    ): SidebarItemTreeDto[] {
      return sidebarTree
        .filter((sidebarItem) => sidebarItem.id !== sidebarItemId)
        .map((sidebarItem) => ({
          ...sidebarItem,
          children: sidebarItem.children
            ? this.removeFromTree(sidebarItem.children, sidebarItemId)
            : [],
        }));
    },

    applySidebarReorderLocally(
      sidebarItems: Array<{ id?: string; parentId?: string; order: number }>,
    ) {
      const reorderMap = new Map(
        (sidebarItems || [])
          .filter(
            (sidebarItem) =>
              typeof sidebarItem?.id === "string" && sidebarItem.id,
          )
          .map((sidebarItem) => [
            sidebarItem.id as string,
            {
              parentId: sidebarItem.parentId || undefined,
              order: sidebarItem.order,
            },
          ]),
      );

      if (reorderMap.size === 0) {
        return;
      }

      this.sidebarItems = this.sidebarItems.map((sidebarItem) => {
        const sidebarItemId = sidebarItem.id || undefined;

        if (!sidebarItemId || !reorderMap.has(sidebarItemId)) {
          return sidebarItem;
        }

        const reorderEntry = reorderMap.get(sidebarItemId)!;

        return {
          ...sidebarItem,
          parentId: reorderEntry.parentId,
          order: reorderEntry.order,
        };
      });

      if (
        this.selectedSidebarItem?.id &&
        reorderMap.has(this.selectedSidebarItem.id)
      ) {
        const reorderEntry = reorderMap.get(this.selectedSidebarItem.id)!;
        this.selectedSidebarItem = {
          ...this.selectedSidebarItem,
          parentId: reorderEntry.parentId,
          order: reorderEntry.order,
        };
      }

      const buildTree = (
        items: SidebarItemDto[],
        parentId?: string,
      ): SidebarItemTreeDto[] => {
        return items
          .filter(
            (sidebarItem) => (sidebarItem.parentId || undefined) === parentId,
          )
          .sort((a, b) => {
            const orderDiff = (a.order || 0) - (b.order || 0);

            if (orderDiff !== 0) {
              return orderDiff;
            }

            return (a.title || "").localeCompare(b.title || "");
          })
          .map((sidebarItem) => ({
            id: sidebarItem.id,
            parentId: sidebarItem.parentId,
            title: sidebarItem.title,
            icon: sidebarItem.icon,
            link: sidebarItem.link,
            order: sidebarItem.order || 0,
            children: buildTree(items, sidebarItem.id || undefined),
          }));
      };

      this.sidebarTree = buildTree(this.sidebarItems);

      if (this.visibleSidebarTree.length > 0) {
        const visibleIds = new Set(
          this.flattenSidebarTree(this.visibleSidebarTree)
            .map((sidebarItem) => sidebarItem.id)
            .filter((sidebarItemId): sidebarItemId is string =>
              Boolean(sidebarItemId),
            ),
        );

        const visibleItems = this.sidebarItems.filter(
          (sidebarItem) => sidebarItem.id && visibleIds.has(sidebarItem.id),
        );

        this.visibleSidebarTree = buildTree(visibleItems);
      }
    },

    flattenSidebarTree(
      sidebarTree: SidebarItemTreeDto[],
    ): SidebarItemTreeDto[] {
      return (sidebarTree || []).flatMap((sidebarItem) => [
        sidebarItem,
        ...this.flattenSidebarTree(sidebarItem.children || []),
      ]);
    },
  },
});
